import React, { forwardRef, MutableRefObject, useCallback, useImperativeHandle, useMemo, useRef } from 'react'
// import './send-notification-editor.scss'

import { useJourneyContext } from '../context'
import { parseNotificationResponse } from '../../notifications/helpers/notification-helpers'

import { useService } from '@pushly/aqe/lib/hooks'
import { NotificationBuilderRef } from '../../../components/notification-builder/notification-builder'
import { NotificationBuilderLevel } from '../../../components/notification-builder/enums'
import type { INotificationBuilderState } from '../../../components/notification-builder/interfaces/notification-builder-state.interface'
import {
    buildSubmittedNotificationPackages,
    validateSubmit as validateNotificationBuilder,
} from '../../../components/notification-builder/helpers'
import {
    EditableNode,
    getItemStrategy,
    isSendWithItemStrategyActionStep,
    JourneyNode,
    NodeEditorRef,
} from '../types/journey-nodes'
import type { IActionStep } from '@pushly/models/lib/structs/journeys'
import { extractJourneyTrigger, getJourneyTriggerItemType } from '../../../_utils/journeys'
import { TriggerType } from '@pushly/models/lib/enums/journeys'
import { ExternalEventTypeService } from '../../../services/external-event-type.service'
import { findRegisteredEventType } from '../external-events/event-type-selection'
import {
    buildEventMacroOptions,
    EVENT_MACRO_GROUP,
    macroCategoriesForTrigger,
} from '../external-events/event-macro-options'
import { useItemStrategyOptions } from '../hooks/use-item-strategy-options'
import type { JourneyNotificationBuilderConfig } from './types'
import { MacroType } from '@pushly/aqe/lib/components'
import { DomainNotificationBuilderView } from '../../../components/notification-builder/views/domain-notification-builder-view'
import { JourneyBuilderMode } from '../enums'
import { handleNodeParamsUpdate } from '../hooks/use-submit-action'
import { stripUndefined } from '../../../_utils/strip-undefined'
import { noop } from '@pushly/aqe/lib/utils/noop'
import { simpleNotification } from '../../../_utils/utils'
import {
    collectReferencedEventPaths,
    describeUndeclaredEventProperties,
    undeclaredEventPropertyPaths,
} from '../external-events/event-macro-validation'
import { NotificationCatalogItemPickProps } from '../../../components/notification-builder/views/internals'

type SendNotificationEditorProps = {
    ref?: MutableRefObject<NodeEditorRef>
    node: JourneyNode<IActionStep>
    macroCategories?: { urlFields?: MacroType[]; messageFields?: MacroType[] }
    builderProps?: Partial<JourneyNotificationBuilderConfig>
}

export const SendNotificationEditor = forwardRef<NodeEditorRef, SendNotificationEditorProps>((props, ref) => {
    const { node } = props

    const [state, _dispatch] = useJourneyContext()

    const eventTypeSvc = useService(ExternalEventTypeService)

    const journey = state.journey
    const domain = state.domain

    const action = node.data.step

    const notification = action.configuration.params.notification
    const notificationId = action.configuration.params.notification_id

    const hasItemStrategy = isSendWithItemStrategyActionStep(action)
    const currentSelectedStrategy = hasItemStrategy && journey ? getItemStrategy(action) : undefined

    // For trigger-based campaigns, get itemType from trigger; for scheduled, use domain's ecommConfig
    const itemType =
        hasItemStrategy && journey ? getJourneyTriggerItemType(journey) ?? domain?.ecommConfig?.itemType : undefined
    const itemStrategy = useItemStrategyOptions({
        action,
        itemType,
        allNodes: state.nodes,
        currentNodeId: node.id,
        currentSelectedStrategy,
    })

    const trigger = journey ? extractJourneyTrigger(journey) : undefined
    const triggerParams = trigger?.configuration?.params
    const isExternalEventTrigger = trigger?.configuration?.type === TriggerType.EXTERNAL_EVENT
    const eventName = isExternalEventTrigger ? triggerParams?.event_name : undefined
    const schemaVersion = isExternalEventTrigger ? triggerParams?.schema_version : undefined

    /**
     * The event type this journey is triggered by, read once for the composer.
     *
     * Both things that need it are asynchronous in different ways — the macro picker awaits it,
     * and the save-time check awaits it inside a handler — so it is held as the promise rather than
     * as state. That also settles the builder's mount problem: the builder freezes its config when
     * it mounts, so options arriving as a later render would never reach it.
     */
    const triggeringEventType = useMemo(() => {
        if (!isExternalEventTrigger || domain?.id === undefined || !eventName) return undefined

        return (
            eventTypeSvc
                .list(domain.id)
                .then((response) => {
                    const registered = response.ok && Array.isArray(response.data) ? response.data : []
                    return findRegisteredEventType(registered, eventName, schemaVersion)
                })
                // A registry that could not be read judges nothing and offers nothing, which leaves the
                // composer behaving exactly as it does for every other trigger. The service reports its
                // own failure.
                .catch(() => undefined)
        )
    }, [eventTypeSvc, isExternalEventTrigger, domain?.id, eventName, schemaVersion])

    const eventMacroOptions = useMemo(
        () => triggeringEventType?.then((eventType) => ({ [EVENT_MACRO_GROUP]: buildEventMacroOptions(eventType) })),
        [triggeringEventType],
    )

    const builderConfig: JourneyNotificationBuilderConfig = useMemo(() => {
        let parsedNotifications: JourneyNotificationBuilderConfig['notifications']
        if (notification) {
            // plainToInstance (inside parseNotificationResponse) reads data
            // properties from any object — struct instances work fine as input,
            // prototype methods are ignored. No serialization needed.
            const parsed = parseNotificationResponse(notification)

            parsedNotifications = [parsed]
        }

        const config: JourneyNotificationBuilderConfig = {
            domain,
            level: NotificationBuilderLevel.CAMPAIGN,
            mode: notification ? 'edit' : 'create',
            notifications: parsedNotifications,
            defaultChannels: state.journey?.channels,
            defaultSelectedNotifId: notificationId ? Number(notificationId) : undefined,
            showWebhookOptions: false,
        }

        // Event macros are offered only where an event is what triggered the journey, and in a
        // group of their own — folding them in with the domain's discovered profile attributes
        // would present a value that lives for one event as one stored on a subscriber.
        const eventMacroCategories = macroCategoriesForTrigger(trigger?.configuration?.type)
        if (eventMacroCategories) {
            config.macroProps = { categories: eventMacroCategories, dynamicOptions: eventMacroOptions }
        }

        if (itemStrategy.isCatalog) {
            config.catalogItemType = itemType ?? undefined
            config.catalogItemPickStrategyOptions = itemStrategy.options
            config.selectedCatalogItemPickStrategy = itemStrategy.selected

            const existingStrategy = getItemStrategy(node.data.step)
            if (existingStrategy) {
                config.selectedCatalogItemPickStrategy = existingStrategy
            }

            config.macroProps = {
                categories: {
                    urlFields: ['domain', 'device', 'location', 'ecomm', 'notification', 'secrets', 'custom'],
                    messageFields: ['location', 'ecomm', 'secrets', 'custom'],
                },
                imageMacroProps: {
                    label: itemStrategy.templates.imageMacroTagLabel,
                    tag: itemStrategy.templates.imageMacroTag,
                },
            }

            config.onInitialVariantInit = (variant) => {
                const contentModel = variant.getContent().getDefaultContent()
                contentModel.setLandingUrl(itemStrategy.templates.url)
                contentModel.setTitle(itemStrategy.templates.title)
                contentModel.setBody(itemStrategy.templates.body)
                contentModel.setImageUrl(itemStrategy.templates.image)
            }
        }

        return config
    }, [
        itemStrategy,
        itemType,
        notification,
        state.journey?.channels,
        domain,
        trigger?.configuration?.type,
        eventMacroOptions,
    ])

    // Show catalog item options if we have a catalog item type OR if we have item strategy options
    const isCatalogItemNotification =
        !!builderConfig.catalogItemType || (itemStrategy.isCatalog && itemStrategy.options.length > 0)
    if (!domain) return <></>

    const notificationType = isCatalogItemNotification ? 'cart_notification' : 'standard_notification'

    const catalogItemPick: NotificationCatalogItemPickProps | false = useMemo(() => {
        if (!isCatalogItemNotification) return false

        return {
            options: builderConfig.catalogItemPickStrategyOptions ?? [],
            itemType,
            defaultSelectedOption: builderConfig.selectedCatalogItemPickStrategy,
        } satisfies NotificationCatalogItemPickProps
    }, [builderConfig, isCatalogItemNotification])

    const builderRef = useRef<NotificationBuilderRef>(null!)

    // Track whether the user has made any edits to the notification.
    // Snapshot the builder's variant state after initialization completes,
    // then compare at submit time. Same type, same construction path.
    const initialVariantSnapshotRef = useRef<string | null>(null)

    const onSubmit = useCallback<() => Promise<any>>(async () => {
        if (!builderRef.current) {
            console.warn('Builder ref is not initialized, skipping submit action')
            return
        }
        const builderState = builderRef.current.state

        if (state.mode === JourneyBuilderMode.READONLY) {
            return
        }

        const valid = await validateNotificationBuilder(builderState, domain, false, false)
        if (!valid) {
            return
        }

        const { notifications } = buildSubmittedNotificationPackages(domain, builderState)

        if (!notifications.length) {
            return
        }

        const nextItemStrategy = isCatalogItemNotification ? builderState.selectedCatalogItemPickStrategy : undefined
        const notificationParam = notifications[0]

        // If the builder hasn't been touched, preserve the existing notification_id.
        // Compare the current variant state against the snapshot taken after init.
        const currentVariantSnapshot = JSON.stringify(builderState.variants.map((v) => v.serialize()))

        // A message interpolating a property its event type does not declare renders a blank where
        // the reason for sending should be. Reported here, while the author is looking at the
        // message, rather than by the send being abandoned weeks later with nothing saying why.
        // Reported and not refused: the property map is filled in by discovery, so a property the
        // partner has not sent yet is undeclared even when the message is right about it.
        const eventType = await triggeringEventType
        const unrenderable = describeUndeclaredEventProperties(
            undeclaredEventPropertyPaths(collectReferencedEventPaths(currentVariantSnapshot), eventType),
            eventType,
        )
        if (unrenderable) {
            simpleNotification('warning', unrenderable)
        }
        const userEdited =
            initialVariantSnapshotRef.current !== null && currentVariantSnapshot !== initialVariantSnapshotRef.current
        if (!userEdited && notificationId) {
            if (nextItemStrategy !== undefined) {
                return handleNodeParamsUpdate(node, stripUndefined({ item_strategy: nextItemStrategy }))
            }
            return
        }

        // User edited the notification — send the new payload and clear
        // notification_id so the inline payload survives journey-wizard.tsx's
        // dedup logic (which strips `notification` when both fields are present).
        return handleNodeParamsUpdate(
            node,
            stripUndefined({
                notification: notificationParam,
                item_strategy: nextItemStrategy,
            }),
            { clearNotificationId: !!notificationId },
        )
    }, [
        domain,
        isCatalogItemNotification,
        node,
        state.mode,
        triggeringEventType,
        validateNotificationBuilder,
        buildSubmittedNotificationPackages,
    ])

    useImperativeHandle(ref, () => ({ onSubmit }))

    const builderProps = {
        ...builderConfig,
        ...props.builderProps,
        onChange: (builderState: INotificationBuilderState) => {
            // Snapshot the variant state once the builder finishes loading.
            // At submit time, we compare the current variant state against
            // this snapshot to detect user edits.
            if (
                initialVariantSnapshotRef.current === null &&
                !builderState.loading &&
                builderState.initialNotificationStateLoaded
            ) {
                initialVariantSnapshotRef.current = JSON.stringify(builderState.variants.map((v) => v.serialize()))
            }
        },
    }

    return (
        <div id="send-notification-editor">
            <DomainNotificationBuilderView
                ref={builderRef}
                formType="send"
                formMode="create"
                journey={journey}
                journeyNotificationType={notificationType}
                isInDrawer={true}
                catalogItemPick={catalogItemPick}
                hidePageHeader={true}
                hidePushlyAiToggle={true}
                hidePageAnalysisToggle={true}
                hideAudienceSelection={true}
                hideEstimatedReach={true}
                hideDeliveryChannels={true}
                hideScheduling={true}
                hideSubmit={true}
                onSubmit={noop}
                onCancel={noop}
                builderProps={{
                    ...builderProps,
                    macroProps: builderProps.macroProps,
                }}
            />
        </div>
    )
})
